import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, ClockIcon, FilterIcon, SearchIcon, SortAscIcon, TagIcon, XCircleIcon, AlertTriangleIcon, CalendarIcon, RefreshCwIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, ArrowUpCircleIcon, ArrowDownCircleIcon, ClipboardListIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
// Task status types
type TaskStatus = 'Pending' | 'In Progress' | 'Completed' | 'Cancelled' | 'Overdue';
// Task priority types
type TaskPriority = 'Low' | 'Medium' | 'High' | 'Urgent';
// Module types
type TaskModule = 'PO' | 'VM' | 'AP' | 'CM' | 'EM' | 'DI' | 'OCR' | 'Financial' | 'Settings';
// Task interface
interface Task {
  id: string;
  title: string;
  description: string;
  module: TaskModule;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string;
  assignedTo: string;
  assignedBy: string;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  relatedEntityName?: string;
  tags?: string[];
}
export function TasksManagement({
  locale = 'en-US'
}) {
  // State for tasks and filters
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Filter states
  const [filters, setFilters] = useState({
    modules: [] as TaskModule[],
    statuses: [] as TaskStatus[],
    priorities: [] as TaskPriority[],
    dateRange: '',
    startDate: '',
    endDate: '',
    tags: [] as string[],
    overdueOnly: false,
    assignedToMe: true
  });
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Task;
    direction: 'ascending' | 'descending';
  }>({
    key: 'dueDate',
    direction: 'ascending'
  });
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  // Sample tasks data - in a real app, this would come from an API
  const sampleTasks: Task[] = [{
    id: 'task-001',
    title: 'Review Purchase Order #PO-2023-005',
    description: 'Review and approve the purchase order for office supplies',
    module: 'PO',
    status: 'Pending',
    priority: 'Medium',
    dueDate: '2023-10-30T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'John Smith',
    createdAt: '2023-10-20T09:30:00Z',
    relatedEntityId: 'PO-2023-005',
    relatedEntityType: 'PurchaseOrder',
    relatedEntityName: 'Office Supplies Order',
    tags: ['review', 'approval', 'supplies']
  }, {
    id: 'task-002',
    title: 'Validate Vendor Information',
    description: 'Verify new vendor compliance documents and contact information',
    module: 'VM',
    status: 'In Progress',
    priority: 'High',
    dueDate: '2023-10-25T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'Sarah Johnson',
    createdAt: '2023-10-18T14:15:00Z',
    updatedAt: '2023-10-19T10:20:00Z',
    relatedEntityId: 'VENDOR-2023-042',
    relatedEntityType: 'Vendor',
    relatedEntityName: 'Acme Supplies Inc.',
    tags: ['verification', 'compliance', 'onboarding']
  }, {
    id: 'task-003',
    title: 'Process Invoice Exception',
    description: 'Review and resolve the exception for invoice #INV-2023-108',
    module: 'AP',
    status: 'Overdue',
    priority: 'Urgent',
    dueDate: '2023-10-15T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'Michael Brown',
    createdAt: '2023-10-10T11:45:00Z',
    relatedEntityId: 'INV-2023-108',
    relatedEntityType: 'Invoice',
    relatedEntityName: 'Server Maintenance Services',
    tags: ['exception', 'invoice', 'urgent']
  }, {
    id: 'task-004',
    title: 'Update Contract Milestone',
    description: 'Update the milestone completion status for Project Alpha contract',
    module: 'CM',
    status: 'Completed',
    priority: 'Medium',
    dueDate: '2023-10-22T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'Jennifer Lee',
    createdAt: '2023-10-19T09:00:00Z',
    completedAt: '2023-10-21T16:30:00Z',
    relatedEntityId: 'CONTRACT-2023-015',
    relatedEntityType: 'Contract',
    relatedEntityName: 'Project Alpha Development',
    tags: ['milestone', 'update', 'contract']
  }, {
    id: 'task-005',
    title: 'Approve Expense Claim',
    description: 'Review and approve expense claim for business trip',
    module: 'EM',
    status: 'Pending',
    priority: 'Low',
    dueDate: '2023-11-05T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'David Wilson',
    createdAt: '2023-10-22T10:15:00Z',
    relatedEntityId: 'EXP-2023-056',
    relatedEntityType: 'ExpenseClaim',
    relatedEntityName: 'Business Trip - New York',
    tags: ['approval', 'expense', 'travel']
  }, {
    id: 'task-006',
    title: 'Review OCR Extraction Results',
    description: 'Validate OCR extraction results for recent invoice batch',
    module: 'OCR',
    status: 'In Progress',
    priority: 'High',
    dueDate: '2023-10-28T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'Amanda Clark',
    createdAt: '2023-10-23T08:45:00Z',
    updatedAt: '2023-10-23T14:20:00Z',
    relatedEntityId: 'BATCH-2023-045',
    relatedEntityType: 'DocumentBatch',
    relatedEntityName: 'October Invoice Batch',
    tags: ['ocr', 'validation', 'extraction']
  }, {
    id: 'task-007',
    title: 'Configure Budget Alerts',
    description: 'Set up budget threshold alerts for Q4 departmental budgets',
    module: 'Financial',
    status: 'Pending',
    priority: 'Medium',
    dueDate: '2023-10-31T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'Robert Taylor',
    createdAt: '2023-10-24T11:30:00Z',
    relatedEntityId: 'BUDGET-2023-Q4',
    relatedEntityType: 'Budget',
    relatedEntityName: 'Q4 2023 Departmental Budgets',
    tags: ['configuration', 'budget', 'alerts']
  }, {
    id: 'task-008',
    title: 'Update User Permissions',
    description: 'Review and update user permissions for new finance team members',
    module: 'Settings',
    status: 'Cancelled',
    priority: 'Low',
    dueDate: '2023-10-27T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'Charles Martin',
    createdAt: '2023-10-20T15:45:00Z',
    updatedAt: '2023-10-25T09:10:00Z',
    relatedEntityId: 'USER-GROUP-FINANCE',
    relatedEntityType: 'UserGroup',
    relatedEntityName: 'Finance Department Users',
    tags: ['permissions', 'users', 'security']
  }, {
    id: 'task-009',
    title: 'Validate Document Classification',
    description: 'Review and validate AI classification of recent document uploads',
    module: 'DI',
    status: 'Pending',
    priority: 'High',
    dueDate: '2023-10-29T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'Emily Johnson',
    createdAt: '2023-10-24T13:20:00Z',
    relatedEntityId: 'DOC-BATCH-2023-108',
    relatedEntityType: 'DocumentBatch',
    relatedEntityName: 'Mixed Document Batch - October',
    tags: ['validation', 'classification', 'ai']
  }, {
    id: 'task-010',
    title: 'Reconcile Payment Discrepancies',
    description: 'Investigate and resolve payment discrepancies for vendor ABC Corp',
    module: 'AP',
    status: 'In Progress',
    priority: 'Urgent',
    dueDate: '2023-10-26T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'Thomas Wilson',
    createdAt: '2023-10-23T09:45:00Z',
    updatedAt: '2023-10-24T14:30:00Z',
    relatedEntityId: 'VENDOR-ABC-CORP',
    relatedEntityType: 'Vendor',
    relatedEntityName: 'ABC Corporation',
    tags: ['reconciliation', 'payment', 'discrepancy']
  }, {
    id: 'task-011',
    title: 'Approve Purchase Requisition',
    description: 'Review and approve purchase requisition for IT equipment',
    module: 'PO',
    status: 'Completed',
    priority: 'Medium',
    dueDate: '2023-10-20T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'Sarah Johnson',
    createdAt: '2023-10-15T10:30:00Z',
    completedAt: '2023-10-19T16:45:00Z',
    relatedEntityId: 'REQ-2023-042',
    relatedEntityType: 'Requisition',
    relatedEntityName: 'IT Equipment Requisition',
    tags: ['approval', 'requisition', 'it']
  }, {
    id: 'task-012',
    title: 'Review Contract Amendment',
    description: 'Review proposed amendments to the service agreement with XYZ Services',
    module: 'CM',
    status: 'Overdue',
    priority: 'High',
    dueDate: '2023-10-18T17:00:00Z',
    assignedTo: 'Admin',
    assignedBy: 'Jennifer Lee',
    createdAt: '2023-10-10T14:15:00Z',
    relatedEntityId: 'CONTRACT-2023-008',
    relatedEntityType: 'Contract',
    relatedEntityName: 'XYZ Services Agreement',
    tags: ['amendment', 'contract', 'review']
  }];
  // Initialize tasks on component mount
  useEffect(() => {
    // Simulate API call
    setIsLoading(true);
    setTimeout(() => {
      setTasks(sampleTasks);
      setFilteredTasks(sampleTasks);
      setIsLoading(false);
    }, 800);
  }, []);
  // Update filtered tasks when filters or search term changes
  useEffect(() => {
    let result = [...tasks];
    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(task => task.title.toLowerCase().includes(searchLower) || task.description.toLowerCase().includes(searchLower) || task.relatedEntityName && task.relatedEntityName.toLowerCase().includes(searchLower) || task.tags && task.tags.some(tag => tag.toLowerCase().includes(searchLower)));
    }
    // Apply module filters
    if (filters.modules.length > 0) {
      result = result.filter(task => filters.modules.includes(task.module));
    }
    // Apply status filters
    if (filters.statuses.length > 0) {
      result = result.filter(task => filters.statuses.includes(task.status));
    }
    // Apply priority filters
    if (filters.priorities.length > 0) {
      result = result.filter(task => filters.priorities.includes(task.priority));
    }
    // Apply overdue filter
    if (filters.overdueOnly) {
      result = result.filter(task => task.status === 'Overdue');
    }
    // Apply assigned to me filter
    if (filters.assignedToMe) {
      result = result.filter(task => task.assignedTo === 'Admin'); // In a real app, this would be the current user
    }
    // Apply date range filter
    if (filters.dateRange) {
      const now = new Date();
      let startDate = new Date();
      if (filters.dateRange === 'today') {
        startDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'tomorrow') {
        startDate.setDate(now.getDate() + 1);
        startDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'this-week') {
        const day = now.getDay();
        startDate.setDate(now.getDate() - day);
        startDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'next-week') {
        const day = now.getDay();
        startDate.setDate(now.getDate() - day + 7);
        startDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'this-month') {
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
      } else if (filters.dateRange === 'custom') {
        if (filters.startDate && filters.endDate) {
          const customStartDate = new Date(filters.startDate);
          const customEndDate = new Date(filters.endDate);
          customEndDate.setHours(23, 59, 59, 999);
          result = result.filter(task => {
            const taskDueDate = new Date(task.dueDate);
            return taskDueDate >= customStartDate && taskDueDate <= customEndDate;
          });
          setFilteredTasks(result);
          return;
        }
      }
      result = result.filter(task => {
        const taskDueDate = new Date(task.dueDate);
        return taskDueDate >= startDate;
      });
    }
    // Apply tag filters
    if (filters.tags.length > 0) {
      result = result.filter(task => task.tags && filters.tags.some(tag => task.tags?.includes(tag)));
    }
    // Apply sorting
    result.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    setFilteredTasks(result);
    setCurrentPage(1); // Reset to first page when filtering
  }, [tasks, searchTerm, filters, sortConfig]);
  // Update total pages when filtered tasks or page size changes
  useEffect(() => {
    setTotalPages(Math.ceil(filteredTasks.length / pageSize));
  }, [filteredTasks, pageSize]);
  // Get current page tasks
  const getCurrentPageTasks = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredTasks.slice(startIndex, endIndex);
  };
  // Handle sort request
  const requestSort = (key: keyof Task) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({
      key,
      direction
    });
  };
  // Toggle filter for modules, statuses, and priorities
  const toggleFilter = (filterType: 'modules' | 'statuses' | 'priorities', value: TaskModule | TaskStatus | TaskPriority) => {
    setFilters(prev => {
      const currentFilters = prev[filterType] as any[];
      return {
        ...prev,
        [filterType]: currentFilters.includes(value) ? currentFilters.filter(f => f !== value) : [...currentFilters, value]
      };
    });
  };
  // Toggle tag filter
  const toggleTagFilter = (tag: string) => {
    setFilters(prev => {
      const currentTags = prev.tags;
      return {
        ...prev,
        tags: currentTags.includes(tag) ? currentTags.filter(t => t !== tag) : [...currentTags, tag]
      };
    });
  };
  // Reset all filters
  const resetFilters = () => {
    setFilters({
      modules: [],
      statuses: [],
      priorities: [],
      dateRange: '',
      startDate: '',
      endDate: '',
      tags: [],
      overdueOnly: false,
      assignedToMe: true
    });
    setSearchTerm('');
  };
  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  // Format date based on locale
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Check if a task is overdue
  const isOverdue = (dueDate: string) => {
    const now = new Date();
    const taskDueDate = new Date(dueDate);
    return taskDueDate < now;
  };
  // Get all available tags from tasks
  const getAllTags = () => {
    const tagSet = new Set<string>();
    tasks.forEach(task => {
      if (task.tags) {
        task.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet);
  };
  // Get status badge color and icon
  const getStatusBadge = (status: TaskStatus) => {
    switch (status) {
      case 'Completed':
        return {
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          icon: <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-5607" />
        };
      case 'In Progress':
        return {
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          icon: <ClockIcon className="h-4 w-4 text-blue-500 mr-1" data-id="element-5608" />
        };
      case 'Pending':
        return {
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          icon: <ClockIcon className="h-4 w-4 text-yellow-500 mr-1" data-id="element-5609" />
        };
      case 'Cancelled':
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          icon: <XCircleIcon className="h-4 w-4 text-gray-500 mr-1" data-id="element-5610" />
        };
      case 'Overdue':
        return {
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          icon: <AlertTriangleIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-5611" />
        };
      default:
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          icon: null
        };
    }
  };
  // Get priority badge color and icon
  const getPriorityBadge = (priority: TaskPriority) => {
    switch (priority) {
      case 'Urgent':
        return {
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          icon: <ArrowUpCircleIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-5612" />
        };
      case 'High':
        return {
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-800',
          icon: <ArrowUpCircleIcon className="h-4 w-4 text-orange-500 mr-1" data-id="element-5613" />
        };
      case 'Medium':
        return {
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          icon: null
        };
      case 'Low':
        return {
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          icon: <ArrowDownCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-5614" />
        };
      default:
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          icon: null
        };
    }
  };
  // Get module badge color
  const getModuleBadge = (module: TaskModule) => {
    switch (module) {
      case 'PO':
        return 'bg-indigo-100 text-indigo-800';
      case 'VM':
        return 'bg-purple-100 text-purple-800';
      case 'AP':
        return 'bg-blue-100 text-blue-800';
      case 'CM':
        return 'bg-teal-100 text-teal-800';
      case 'EM':
        return 'bg-emerald-100 text-emerald-800';
      case 'DI':
        return 'bg-amber-100 text-amber-800';
      case 'OCR':
        return 'bg-fuchsia-100 text-fuchsia-800';
      case 'Financial':
        return 'bg-cyan-100 text-cyan-800';
      case 'Settings':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Handle task completion
  const handleCompleteTask = (taskId: string) => {
    setTasks(prevTasks => prevTasks.map(task => task.id === taskId ? {
      ...task,
      status: 'Completed',
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    } : task));
  };
  // Handle task cancellation
  const handleCancelTask = (taskId: string) => {
    setTasks(prevTasks => prevTasks.map(task => task.id === taskId ? {
      ...task,
      status: 'Cancelled',
      updatedAt: new Date().toISOString()
    } : task));
  };
  return <div className="space-y-6" data-id="element-5615">
      <div className="flex justify-between items-center" data-id="element-5616">
        <div data-id="element-5617">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-5618">Tasks</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5619">
            View and manage your tasks across all modules
          </p>
        </div>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-5620">
        <div className="px-4 py-5 sm:p-6" data-id="element-5621">
          {/* Search and filter controls */}
          <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4 mb-6" data-id="element-5622">
            <div className="relative flex-1 max-w-lg" data-id="element-5623">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5624">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-5625" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Search tasks by title, description, or tags..." data-id="element-5626" />
            </div>
            <div className="flex space-x-2" data-id="element-5627">
              <button onClick={() => setShowFilters(!showFilters)} className={`inline-flex items-center px-3 py-2 border ${showFilters ? 'border-indigo-500 text-indigo-700' : 'border-gray-300 text-gray-700'} rounded-md shadow-sm text-sm font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-5628">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-5629" />
                Filters
                {(filters.modules.length > 0 || filters.statuses.length > 0 || filters.priorities.length > 0 || filters.tags.length > 0 || filters.dateRange || filters.overdueOnly || !filters.assignedToMe) && ` (${filters.modules.length + filters.statuses.length + filters.priorities.length + filters.tags.length + (filters.dateRange ? 1 : 0) + (filters.overdueOnly ? 1 : 0) + (!filters.assignedToMe ? 1 : 0)})`}
              </button>
              {(filters.modules.length > 0 || filters.statuses.length > 0 || filters.priorities.length > 0 || filters.tags.length > 0 || filters.dateRange || filters.overdueOnly || !filters.assignedToMe || searchTerm) && <button onClick={resetFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-5630">
                  <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-5631" />
                  Reset
                </button>}
            </div>
          </div>
          {/* Filter panel */}
          {showFilters && <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-5632">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5633">
                {/* Module filters */}
                <div data-id="element-5634">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-5635">
                    Module
                  </h4>
                  <div className="space-y-2" data-id="element-5636">
                    {(['PO', 'VM', 'AP', 'CM', 'EM', 'DI', 'OCR', 'Financial', 'Settings'] as TaskModule[]).map(module => <div key={module} className="flex items-center" data-id="element-5637">
                        <input id={`filter-module-${module}`} name={`filter-module-${module}`} type="checkbox" checked={filters.modules.includes(module)} onChange={() => toggleFilter('modules', module)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-5638" />
                        <label htmlFor={`filter-module-${module}`} className="ml-2 text-sm text-gray-700" data-id="element-5639">
                          {module}
                        </label>
                      </div>)}
                  </div>
                </div>
                {/* Status filters */}
                <div data-id="element-5640">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-5641">
                    Status
                  </h4>
                  <div className="space-y-2" data-id="element-5642">
                    {(['Pending', 'In Progress', 'Completed', 'Cancelled', 'Overdue'] as TaskStatus[]).map(status => <div key={status} className="flex items-center" data-id="element-5643">
                        <input id={`filter-status-${status}`} name={`filter-status-${status}`} type="checkbox" checked={filters.statuses.includes(status)} onChange={() => toggleFilter('statuses', status)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-5644" />
                        <label htmlFor={`filter-status-${status}`} className="ml-2 text-sm text-gray-700 flex items-center" data-id="element-5645">
                          {getStatusBadge(status).icon}
                          {status}
                        </label>
                      </div>)}
                  </div>
                </div>
                {/* Priority filters */}
                <div data-id="element-5646">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-5647">
                    Priority
                  </h4>
                  <div className="space-y-2" data-id="element-5648">
                    {(['Urgent', 'High', 'Medium', 'Low'] as TaskPriority[]).map(priority => <div key={priority} className="flex items-center" data-id="element-5649">
                        <input id={`filter-priority-${priority}`} name={`filter-priority-${priority}`} type="checkbox" checked={filters.priorities.includes(priority)} onChange={() => toggleFilter('priorities', priority)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-5650" />
                        <label htmlFor={`filter-priority-${priority}`} className="ml-2 text-sm text-gray-700 flex items-center" data-id="element-5651">
                          {getPriorityBadge(priority).icon}
                          {priority}
                        </label>
                      </div>)}
                  </div>
                </div>
              </div>
              {/* Second row of filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-5652">
                {/* Due date filters */}
                <div data-id="element-5653">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-5654">
                    Due Date
                  </h4>
                  <div className="space-y-2" data-id="element-5655">
                    <div data-id="element-5656">
                      <select value={filters.dateRange} onChange={e => {
                    setFilters({
                      ...filters,
                      dateRange: e.target.value
                    });
                  }} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-5657">
                        <option value="" data-id="element-5658">Any time</option>
                        <option value="today" data-id="element-5659">Due today</option>
                        <option value="tomorrow" data-id="element-5660">Due tomorrow</option>
                        <option value="this-week" data-id="element-5661">Due this week</option>
                        <option value="next-week" data-id="element-5662">Due next week</option>
                        <option value="this-month" data-id="element-5663">Due this month</option>
                        <option value="custom" data-id="element-5664">Custom range</option>
                      </select>
                    </div>
                    {filters.dateRange === 'custom' && <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-5665">
                        <div data-id="element-5666">
                          <label htmlFor="start-date" className="block text-xs font-medium text-gray-700" data-id="element-5667">
                            Start Date
                          </label>
                          <input type="date" id="start-date" value={filters.startDate} onChange={e => {
                      setFilters({
                        ...filters,
                        startDate: e.target.value
                      });
                    }} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-5668" />
                        </div>
                        <div data-id="element-5669">
                          <label htmlFor="end-date" className="block text-xs font-medium text-gray-700" data-id="element-5670">
                            End Date
                          </label>
                          <input type="date" id="end-date" value={filters.endDate} onChange={e => {
                      setFilters({
                        ...filters,
                        endDate: e.target.value
                      });
                    }} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-5671" />
                        </div>
                      </div>}
                  </div>
                </div>
                {/* Other filters */}
                <div data-id="element-5672">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-5673">
                    Other Filters
                  </h4>
                  <div className="space-y-2" data-id="element-5674">
                    <div className="flex items-center" data-id="element-5675">
                      <input id="filter-overdue" name="filter-overdue" type="checkbox" checked={filters.overdueOnly} onChange={() => {
                    setFilters({
                      ...filters,
                      overdueOnly: !filters.overdueOnly
                    });
                  }} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-5676" />
                      <label htmlFor="filter-overdue" className="ml-2 text-sm text-gray-700 flex items-center" data-id="element-5677">
                        <AlertTriangleIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-5678" />
                        Overdue only
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-5679">
                      <input id="filter-assigned-to-me" name="filter-assigned-to-me" type="checkbox" checked={filters.assignedToMe} onChange={() => {
                    setFilters({
                      ...filters,
                      assignedToMe: !filters.assignedToMe
                    });
                  }} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-5680" />
                      <label htmlFor="filter-assigned-to-me" className="ml-2 text-sm text-gray-700" data-id="element-5681">
                        Assigned to me
                      </label>
                    </div>
                  </div>
                  {/* Tags filter */}
                  <div className="mt-4" data-id="element-5682">
                    <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-5683">
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-2" data-id="element-5684">
                      {getAllTags().map(tag => <button key={tag} onClick={() => toggleTagFilter(tag)} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${filters.tags.includes(tag) ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-5685">
                          <TagIcon className="h-3 w-3 mr-1" data-id="element-5686" />
                          {tag}
                        </button>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {/* Tasks count and pagination info */}
          <div className="flex justify-between items-center mb-4" data-id="element-5687">
            <div className="text-sm text-gray-700" data-id="element-5688">
              Showing{' '}
              <span className="font-medium" data-id="element-5689">
                {filteredTasks.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}
              </span>{' '}
              to{' '}
              <span className="font-medium" data-id="element-5690">
                {Math.min(currentPage * pageSize, filteredTasks.length)}
              </span>{' '}
              of <span className="font-medium" data-id="element-5691">{filteredTasks.length}</span>{' '}
              tasks
            </div>
            <div className="flex items-center space-x-2" data-id="element-5692">
              <span className="text-sm text-gray-700" data-id="element-5693">Rows per page:</span>
              <select value={pageSize} onChange={e => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing page size
            }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-5694">
                <option value={5} data-id="element-5695">5</option>
                <option value={10} data-id="element-5696">10</option>
                <option value={25} data-id="element-5697">25</option>
                <option value={50} data-id="element-5698">50</option>
              </select>
            </div>
          </div>
          {/* Tasks table */}
          {isLoading ? <div className="flex justify-center items-center py-20" data-id="element-5699">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" data-id="element-5700"></div>
            </div> : filteredTasks.length === 0 ? <div className="text-center py-10" data-id="element-5701">
              <ClipboardListIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-5702" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-5703">
                No tasks found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-5704">
                Try adjusting your search or filter criteria.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-5705">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-5706">
                <thead className="bg-gray-50" data-id="element-5707">
                  <tr data-id="element-5708">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('title')} data-id="element-5709">
                      <div className="flex items-center" data-id="element-5710">
                        Task
                        {sortConfig.key === 'title' && <span className="ml-1" data-id="element-5711">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-5712" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-5713" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('module')} data-id="element-5714">
                      <div className="flex items-center" data-id="element-5715">
                        Module
                        {sortConfig.key === 'module' && <span className="ml-1" data-id="element-5716">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-5717" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-5718" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('status')} data-id="element-5719">
                      <div className="flex items-center" data-id="element-5720">
                        Status
                        {sortConfig.key === 'status' && <span className="ml-1" data-id="element-5721">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-5722" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-5723" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('priority')} data-id="element-5724">
                      <div className="flex items-center" data-id="element-5725">
                        Priority
                        {sortConfig.key === 'priority' && <span className="ml-1" data-id="element-5726">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-5727" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-5728" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('dueDate')} data-id="element-5729">
                      <div className="flex items-center" data-id="element-5730">
                        Due Date
                        {sortConfig.key === 'dueDate' && <span className="ml-1" data-id="element-5731">
                            {sortConfig.direction === 'ascending' ? <ChevronUpIcon className="h-4 w-4" data-id="element-5732" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-5733" />}
                          </span>}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5734">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-5735">
                  {getCurrentPageTasks().map(task => {
                const statusBadge = getStatusBadge(task.status);
                const priorityBadge = getPriorityBadge(task.priority);
                const moduleBadge = getModuleBadge(task.module);
                const taskDueDate = new Date(task.dueDate);
                const isTaskOverdue = isOverdue(task.dueDate) && task.status !== 'Completed' && task.status !== 'Cancelled';
                return <tr key={task.id} data-id="element-5736">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5737">
                          <div className="flex items-center" data-id="element-5738">
                            <div className="ml-0" data-id="element-5739">
                              <div className="text-sm font-medium text-gray-900" data-id="element-5740">
                                {task.title}
                              </div>
                              <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-5741">
                                {task.description}
                              </div>
                              {task.tags && <div className="flex flex-wrap gap-1 mt-1" data-id="element-5742">
                                  {task.tags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5743">
                                      <TagIcon className="h-3 w-3 mr-0.5" data-id="element-5744" />
                                      {tag}
                                    </span>)}
                                </div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5745">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${moduleBadge}`} data-id="element-5746">
                            {task.module}
                          </span>
                          {task.relatedEntityName && <div className="text-xs text-gray-500 mt-1" data-id="element-5747">
                              {task.relatedEntityName}
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5748">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.bgColor} ${statusBadge.textColor}`} data-id="element-5749">
                            {statusBadge.icon}
                            {task.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5750">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityBadge.bgColor} ${priorityBadge.textColor}`} data-id="element-5751">
                            {priorityBadge.icon}
                            {task.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-5752">
                          <div className="flex items-center" data-id="element-5753">
                            <CalendarIcon className={`h-4 w-4 mr-1 ${isTaskOverdue ? 'text-red-500' : 'text-gray-400'}`} data-id="element-5754" />
                            <span className={`text-sm ${isTaskOverdue ? 'text-red-500 font-medium' : 'text-gray-500'}`} data-id="element-5755">
                              {formatDate(task.dueDate)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5756">
                          <div className="flex justify-end space-x-2" data-id="element-5757">
                            {task.status !== 'Completed' && task.status !== 'Cancelled' && <button onClick={() => handleCompleteTask(task.id)} className="text-green-600 hover:text-green-900" title="Mark as complete" data-id="element-5758">
                                  <CheckIcon className="h-5 w-5" data-id="element-5759" />
                                </button>}
                            {task.status !== 'Cancelled' && task.status !== 'Completed' && <button onClick={() => handleCancelTask(task.id)} className="text-red-600 hover:text-red-900" title="Cancel task" data-id="element-5760">
                                  <XIcon className="h-5 w-5" data-id="element-5761" />
                                </button>}
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
          {/* Pagination controls */}
          {filteredTasks.length > 0 && totalPages > 1 && <div className="mt-4 flex items-center justify-between" data-id="element-5762">
              <div className="flex-1 flex justify-between sm:hidden" data-id="element-5763">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-5764">
                  Previous
                </button>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-5765">
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-5766">
                <div data-id="element-5767">
                  <p className="text-sm text-gray-700" data-id="element-5768">
                    Page <span className="font-medium" data-id="element-5769">{currentPage}</span> of{' '}
                    <span className="font-medium" data-id="element-5770">{totalPages}</span>
                  </p>
                </div>
                <div data-id="element-5771">
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-5772">
                    <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-5773">
                      <span className="sr-only" data-id="element-5774">First</span>
                      <span className="h-5 w-5 flex justify-center items-center" aria-hidden="true" data-id="element-5775">
                        &laquo;
                      </span>
                    </button>
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-5776">
                      <span className="sr-only" data-id="element-5777">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-5778" />
                    </button>
                    {/* Page numbers */}
                    {Array.from({
                  length: Math.min(5, totalPages)
                }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    // If 5 or fewer pages, show all
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    // If near the start
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    // If near the end
                    pageNum = totalPages - 4 + i;
                  } else {
                    // If in the middle
                    pageNum = currentPage - 2 + i;
                  }
                  return <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-5779">
                            {pageNum}
                          </button>;
                })}
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-5780">
                      <span className="sr-only" data-id="element-5781">Next</span>
                      <ChevronUpIcon className="h-5 w-5 rotate-90" aria-hidden="true" data-id="element-5782" />
                    </button>
                    <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-5783">
                      <span className="sr-only" data-id="element-5784">Last</span>
                      <span className="h-5 w-5 flex justify-center items-center" aria-hidden="true" data-id="element-5785">
                        &raquo;
                      </span>
                    </button>
                  </nav>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}